import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { motion, AnimatePresence } from "framer-motion";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price: Low to High" },
  { key: "price-desc", label: "Price: High to Low" },
];

const TITLES = { all: "All", blankets: "Blankets", towels: "Towels", fragrance: "Fragrance", collections: "Collections" };

export default function Shop() {
  const { category } = useParams();
  const tag = category || "all";
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [panel, setPanel] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { tag } }).then((r) => setProducts(r.data));
  }, [tag]);

  const title = TITLES[tag] || "All";
  const sorted = [...products].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    return (b.featured === true) - (a.featured === true);
  });

  return (
    <div data-testid="shop-page" className="pt-16">
      <button data-testid="filters-tag" onClick={() => setPanel(true)}
        className="fixed left-0 top-1/2 z-30 origin-left -translate-y-1/2 uppercase text-[11px] tracking-[0.12em] text-black link-hover"
        style={{ transform: "translateY(-50%) rotate(-90deg) translateX(-50%)" }}>
        Filters
      </button>

      <div className="px-6 md:px-10 lg:px-20 pt-16">
        <Reveal>
          <p className="util-link text-[11px] caption mb-4">Shop</p>
          <h1 data-testid="shop-title" className="font-display text-5xl sm:text-7xl uppercase leading-[0.9]">{title}</h1>
          <p className="caption text-[12px] mt-6">{sorted.length} products</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {sorted.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>

        {sorted.length === 0 && <p className="caption py-24 text-center text-sm">No products found.</p>}
      </div>

      <AnimatePresence>
        {panel && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setPanel(false)} className="fixed inset-0 z-[60] bg-black/30" />
            <motion.aside data-testid="filter-panel"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 left-0 z-[70] h-full w-full sm:w-[380px] bg-white flex flex-col">
              <div className="flex items-center justify-between px-8 py-6 border-b border-border">
                <h3 className="product-title text-[13px]">Sort</h3>
                <button data-testid="filter-close" onClick={() => setPanel(false)} className="util-link text-sm link-hover">Close</button>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-8">
                <p className="util-link text-[11px] caption mb-5">Sort by</p>
                <div className="space-y-3">
                  {SORTS.map((s) => (
                    <button key={s.key} data-testid={`sort-${s.key}`} onClick={() => setSort(s.key)}
                      className={`block text-left text-[13px] link-hover ${sort === s.key ? "border-b border-black pb-0.5" : ""}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-border px-8 py-6">
                <button data-testid="filter-apply" onClick={() => setPanel(false)}
                  className="w-full bg-black text-white py-4 product-title text-[13px] link-hover">
                  Show {sorted.length} products
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
